import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { Close } from "../icons";
import Input from "../common/Input";
import { 
  useLazyGetCentralLowStockQuery, 
  useLazyGetCampusLowStockQuery,
  useLazyGetAdminCampusLowStockQuery
} from "../../store/api/inventoryApi";
import { useGetCategoriesQuery } from "../../store/api/categoryApi";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function LowStockModal({ open, onClose, type = "central", campusId }) {
  const [threshold, setThreshold] = useState("10");
  const [category, setCategory] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [limit, setLimit] = useState("10");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: categoriesData } = useGetCategoriesQuery();
  const categories = categoriesData?.data?.categories || [];

  const [triggerCentral, centralState] = useLazyGetCentralLowStockQuery();
  const [triggerCampus, campusState] = useLazyGetCampusLowStockQuery();
  const [triggerAdmin, adminState] = useLazyGetAdminCampusLowStockQuery();

  const trigger = type === "central" ? triggerCentral : type === "campus" ? triggerCampus : triggerAdmin;
  const data = type === "central" ? centralState.data : type === "campus" ? campusState.data : adminState.data;
  const isFetching = type === "central" ? centralState.isFetching : type === "campus" ? campusState.isFetching : adminState.isFetching;

  const fetchLowStock = async (currentPage = page) => {
    try {
      const payload = {
        threshold: threshold || 10,
        category: category || undefined,
        showAll,
        limit: showAll ? undefined : limit,
        page: showAll ? undefined : currentPage,
        search: debouncedSearch || undefined,
      };
      if (type === "campus" || type === "admin") {
        payload.campusId = campusId;
      }
      await trigger(payload).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to fetch low stock items");
    }
  };

  const handleView = () => {
    setPage(1);
    fetchLowStock(1);
  };

  const handleNextPage = () => {
    const newPage = page + 1;
    setPage(newPage);
    fetchLowStock(newPage);
  };

  const handlePrevPage = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchLowStock(newPage);
    }
  };

  const handleGenerateReport = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      toast.error("No data available to generate report");
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Low Stock Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Threshold: <= ${threshold}`, 14, 22);

    const tableColumn = ["Item Name", "Category", "Current Stock"];
    const tableRows = [];

    data.data.data.forEach((item) => {
      const itemName = item.itemName || item.itemId?.name || "N/A";
      const categoryName = item.categoryName || item.itemId?.itemCategory?.name || "N/A";
      const itemData = [
        itemName,
        categoryName.charAt(0).toUpperCase() + categoryName.slice(1),
        item.stock?.toString() || "0",
      ];
      tableRows.push(itemData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 25,
    });

    doc.save("Low_Stock_Report.pdf");
  };

  useEffect(() => {
    if (open) {
      // reset state if needed
      setPage(1);
    }
  }, [open]);

  // Handle total pagination correctly
  const totalItems = data?.data?.total || 0;
  const currentLimit = Number(limit) || 10;
  const totalPages = Math.ceil(totalItems / currentLimit);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="max-w-[700px] pt-7 pb-11 px-0"
      centered={false}
    >
      <div className="w-full">
        <div className="flex items-center justify-between mb-4 pl-7 pr-10">
          <div>
            <p className="text-xl font-bold leading-6 text-black">
              Low Stock Items
            </p>
          </div>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <div className="border-t border-[#E5E5E5] pt-4 pl-7 pr-10">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input
              label="Threshold"
              type="number"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="e.g. 10"
            />
            <div>
              <label className="text-sm leading-6 text-black block mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#EEEEEE40] text-sm text-[#878686] placeholder:text-[#878686] border border-[#D9D9D9] outline-none px-4 py-3 rounded-[10px]"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id} className="capitalize">
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input
              label="Search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items..."
            />
            <div className="flex flex-col">
              <label className="text-sm leading-6 text-black block mb-2">
                Pagination
              </label>
              <div className="flex items-center gap-4 h-full">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                    className="accent-[#6D4E8A]"
                  />
                  <span className="text-sm text-black">Show All</span>
                </label>
                {!showAll && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-black">Limit:</span>
                    <input
                      type="number"
                      min="1"
                      className="w-20 bg-[#EEEEEE40] border border-[#D9D9D9] px-2 py-2 rounded-[10px] text-sm outline-none"
                      placeholder="Limit"
                      value={limit}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (val !== "" && Number(val) < 1) val = "1";
                        setLimit(val);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-4">
              <button
                onClick={handleView}
                disabled={isFetching}
                className="bg-[#6D4E8A] text-white px-6 py-2.5 rounded-[5px] text-sm transition hover:bg-[#5a3f75] disabled:opacity-50"
              >
                {isFetching ? "Loading..." : "View"}
              </button>
              {data?.data?.data && data.data.data.length > 0 && (
                <button
                  onClick={handleGenerateReport}
                  className="bg-[#3CA966] text-white px-6 py-2.5 rounded-[5px] text-sm transition hover:bg-[#2b884f]"
                >
                  Generate Report
                </button>
              )}
            </div>

            {/* Pagination Controls */}
            {!showAll && data?.data && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevPage}
                  disabled={page <= 1 || isFetching}
                  className="px-3 py-1 bg-[#EEEEEE40] border border-[#D9D9D9] rounded-[5px] text-sm disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-sm text-black">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page >= totalPages || isFetching || totalPages === 0}
                  className="px-3 py-1 bg-[#EEEEEE40] border border-[#D9D9D9] rounded-[5px] text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {data?.data?.data && (
            <div className="max-h-[300px] overflow-y-auto border border-[#E5E5E5] rounded-[10px]">
              <table className="w-full text-left text-sm text-[#000]">
                <thead className="bg-[#F0F0F0] sticky top-0">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Item Name</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.data.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-center">
                        No items found.
                      </td>
                    </tr>
                  ) : (
                    data.data.data.map((item) => {
                      const itemName = item.itemName || item.itemId?.name || "N/A";
                      const categoryName = item.categoryName || item.itemId?.itemCategory?.name || "N/A";
                      const itemImage = item.itemImage || item.itemId?.itemPicture?.secure_url || "";

                      return (
                        <tr key={item._id} className="border-b border-[#E5E5E5]">
                          <td className="px-4 py-3 flex items-center gap-3">
                            {itemImage ? (
                              <img
                                src={itemImage}
                                alt=""
                                className="w-8 h-8 object-cover rounded"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gray-200 rounded"></div>
                            )}
                            {itemName}
                          </td>
                          <td className="px-4 py-3 capitalize">{categoryName}</td>
                          <td className="px-4 py-3">{item.stock}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
